import { observer } from 'mobx-react-lite';
import React from 'react';

import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { setAvailabilityOverride } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useCalendarStore, useCurrentuser, useTimezone } from '@uplift/contexts';
import { useFirstRef, useIsCurrentProviderLive } from '@uplift/hooks';
import { useICSSubscriptionEventsQuery } from '@uplift/queries';
import { CalendarDateService, DateObject } from '@uplift/services';
import { CalendarStore } from '@uplift/store';
import { NEW_EVENT_ID, UserStatus, TimeRange } from '@uplift/types';
import {
  CalendarNewEvent, CalendarRef, ICSConflictsResolutionModal, ScheduleCalendar, ScrollableView
} from '@uplift/ui-v2';

import { AddSessionPopover } from './add-session-popover.component';
import { CalendarMetrics } from './calendar-metrics.component';
import { CalendarModals } from './calendar-modals';
import { ScheduleSidebar } from './sidebar';
import { formatUserFullName } from '@uplift/utils';

export const useStyles = makeStyles()((theme) => ({
  sideBar: {
    background: theme.palette.grey[50],
    maxWidth: '294px',
    width: '100%',
    display: 'flex',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  calendarWrapper: {
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(3),
    paddingRight: 0,
    flex: 1,
  },
}));


export type ProviderSessionCalendarProps = {
  calendarRef: React.MutableRefObject<CalendarRef>;
  newEvent?: CalendarNewEvent;
  setNewEvent?: (event: CalendarNewEvent) => void;
};


export const ProviderSessionsCalendar: React.FC<ProviderSessionCalendarProps> = observer(({ calendarRef, newEvent, setNewEvent }) => {
  const me = useCurrentuser();
  const isProviderInLiveStatus = useIsCurrentProviderLive(me);

  const calendarStore = useCalendarStore();
  const calendarApi = calendarStore.calendarApi;
  const [ createAppointmentOpen, setCreateAppointmentOpen ] = React.useState(false);
  const [ ICSResolutionOpened, setICSResolutionOpened ] = React.useState(false);
  const [ createEventAnchor, setCreateEventAnchor ] = useFirstRef<HTMLDivElement>();
  const timezone = useTimezone();
  const calendarStoreRef = React.useRef<CalendarStore>(calendarStore);

  calendarStoreRef.current = calendarStore;

  const { data: icsSubscription } = useICSSubscriptionEventsQuery(me.id, {
    enabled: true,
    refetchOnMount: 'always',
  });

  const handleCloseCreateEvent = React.useCallback(() => {
    setNewEvent(undefined);
  }, []);

  const onElementCreate = React.useCallback((e: HTMLDivElement | null) => {
    if (e) {
      setCreateEventAnchor(e);
    } else {
      setCreateEventAnchor(undefined);
    }
  }, []);

  React.useEffect(() => {
    const calendarStore = calendarStoreRef.current;
    const calendarApi = calendarStore?.calendarApi;

    if (newEvent) {
      const event = calendarStoreRef.current?.calendarApi?.getEventById(NEW_EVENT_ID);

      calendarStore?.setNewEvent({
        time: {
          start: newEvent.start,
          end: newEvent.end,
        },
        timezone: timezone,
      });

      if (event) {
        event.setDates(newEvent.start, newEvent.end);
      } else {
        calendarApi.addEvent(newEvent);
      }
      setCreateAppointmentOpen(true);
    } else {
      calendarApi?.getEventById(NEW_EVENT_ID)?.remove();
      setCreateAppointmentOpen(false);
    }
  }, [ newEvent, timezone ]);

  const events = React.useMemo(() => {
    return calendarStore.appointments
      ?.filter(a => a.patient_id)
      .map((appointment) => {
        const patient = calendarStore.patients?.[appointment.patient_id];
        const start = CalendarDateService.date.utc(appointment.from_datetime);
        const end = CalendarDateService.date.utc(appointment.to_datetime);

        return {
          id: appointment.id,
          title: formatUserFullName({
            firstName: patient.first_name,
            lastName: patient.last_name,
            preferredFirstName: patient.preferred_name,
          }),
          start: start.format(),
          end: end.format(),
          extendedProps: {
            fromDatetime: appointment.from_datetime,
            toDatetime: appointment.to_datetime,
            parentId: appointment.appointment_series_id,
            recurring: appointment.recurring,
            state: appointment.state,
            patientId: appointment.patient_id,
            isRegular: patient?.status === UserStatus.regular,
            isMaterialized: appointment.is_materialized,
          },
        };
      });
  }, [ calendarStore.appointments, calendarStore.patients ]);

  const handleAvailabilityDateOverride = React.useCallback(async (date: DateObject, ranges: TimeRange[]) => {
    await setAvailabilityOverride({
      date: date.format(DateFormats.ISO_DATE),
      ranges: ranges,
    });

    calendarStore.loadAllEvents(calendarStore._lastInfo, true);
  }, []);

  return (
    <>
      <ScheduleCalendar
        setNewEvent={setNewEvent}
        calendarRef={calendarRef}
        availabilityEditing={true}
        events={events}
        loadAllEvents={calendarStore.loadAllEvents}
        isCalendarLoading={calendarStore.isCalendarLoading}
        checkSlotAvailability={calendarStore.isRangeAvailableForBooking}
        availableSlots={calendarStore.allowedSlots}
        openModal={calendarStore.openModal}
        setSelectedEvent={calendarStore.setSelectedEvent}
        calendarApi={calendarApi}
        setCalendarApi={calendarStore.setCalendarApi}
        newEventAnchorRef={onElementCreate}
        availabilityTemplate={calendarStore.availabilityTemplate}
        overrides={calendarStore.availabilityOverrides}
        onAvailabilityDateOverride={handleAvailabilityDateOverride}
        icsEvents={icsSubscription?.events ?? []}
        icsConflicts={icsSubscription?.conflicts ?? []}
        onICSConflictsOpen={() => setICSResolutionOpened(true)}
        allowToCreateAppointments={isProviderInLiveStatus}
      />
      <AddSessionPopover
        key={newEvent ? 'open' : 'closed'}
        open={createAppointmentOpen}
        onClose={handleCloseCreateEvent}
        setCurrentEvent={(e) => setNewEvent({ ...newEvent, start: e.startStr, end: e.endStr })}
        anchor={createEventAnchor}
      />
      <ICSConflictsResolutionModal
        open={ICSResolutionOpened}
        onClose={() => setICSResolutionOpened(false)}
        keepMounted
      />
    </>
  );
});


export const CalendarDesktop: React.FC = () => {
  const me = useCurrentuser();
  const isProviderInLiveStatus = useIsCurrentProviderLive(me);
  const { classes } = useStyles();
  const calendarRef = React.useRef<CalendarRef>();
  const [ newEvent, setNewEvent ] = React.useState<CalendarNewEvent>();

  return (
    <>
      <ScrollableView className={classes.sideBar}>
        <Box p={3}>
          <ScheduleSidebar
            calendarRef={calendarRef}
            setNewEvent={setNewEvent}
          />
        </Box>
      </ScrollableView>
      <Box className={classes.content}>
        <Box className={classes.calendarWrapper}>
          <ProviderSessionsCalendar
            newEvent={newEvent}
            setNewEvent={setNewEvent}
            calendarRef={calendarRef}
          />
        </Box>
        {isProviderInLiveStatus && <CalendarMetrics />}
      </Box>
      <CalendarModals />
    </>
  );
};
